// src/admin/pages/Newsletter/Newslettermanagement.tsx
import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Alert,
    Pagination,
    useTheme,
    useMediaQuery,
    Backdrop,
    LinearProgress,
} from '@mui/material';
import {
    Delete,
    Search as SearchIcon,
    Clear as ClearIcon,
    Email as EmailIcon,
    DeleteSweep,
    Refresh,
} from '@mui/icons-material';
import { NewsletterSubscriber } from './newsletterService';
import {
    useNewsletterPaginated,
    useNewsletterStats,
    useNewsletterMutations,
} from '../../../hooks/useNewsletter';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';

const ITEMS_PER_PAGE = 10;

interface ToastState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
    type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

    const lightPalette: Record<string, PaletteEntry> = {
        success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
        error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
        primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
        warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
        secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
        default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
    };

    const darkPalette: Record<string, PaletteEntry> = {
        success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
        error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
        primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
        warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
        secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
        default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
    };

    const palette = isDark ? darkPalette : lightPalette;
    const p = palette[color] ?? palette.default;

    return {
        backgroundColor: p.bg,
        borderColor: p.border,
        color: p.text,
        fontWeight: 600,
        border: `1px solid ${p.border}`,
        '& .MuiChip-icon': { color: p.iconColor },
        '& .MuiChip-label': { color: p.text },
    };
};

interface LoadingOverlayProps {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
    const [rect, setRect] = useState<DOMRect | null>(null);
    const theme = useTheme();

    const overlayBg = theme.palette.mode === 'dark'
        ? 'rgba(26, 29, 36, 0.85)'
        : 'rgba(255, 255, 255, 0.85)';

    React.useEffect(() => {
        const measure = () => {
            if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
        };
        measure();
        const el = wrapperRef.current;
        window.addEventListener('resize', measure);
        el?.addEventListener('scroll', measure);
        return () => {
            window.removeEventListener('resize', measure);
            el?.removeEventListener('scroll', measure);
        };
    }, [wrapperRef]);

    if (!rect) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                backgroundColor: overlayBg,
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1100,
                pointerEvents: 'all',
                borderRadius: 'inherit',
            }}
        >
            <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading subscribers...
                </Typography>
            </Box>
        </Box>
    );
};

const formatDate = (date?: Date): string => {
    if (!date) return '—';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const NewsletterManagement: React.FC = () => {
    // ── UI state ──────────────────────────────────────────────────────────────
    const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

    // ── Pagination & filter state ─────────────────────────────────────────────
    // FIX: Removed all cursor state (lastVisible, firstVisible, cursors).
    // Seek-based pagination only needs the current page number.
    const [currentPage, setCurrentPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // ── Single delete ─────────────────────────────────────────────────────────
    const [deleteTarget, setDeleteTarget] = useState<NewsletterSubscriber | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ── Bulk delete ───────────────────────────────────────────────────────────
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    const { hasPermission, isSuperAdmin } = useAuth();

    const canView = isSuperAdmin || hasPermission('newsletter.view');
    const canManage = isSuperAdmin || hasPermission('newsletter.manage'); // delete, bulk delete

    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // ── React Query hooks ─────────────────────────────────────────────────────
    // FIX: No longer passes a `cursors` object — just page number + search.
    const {
        data: subscribersData,
        isLoading: subscribersLoading,
        isFetching: subscribersIsFetching,
        isError: subscribersIsError,
        error: subscribersErrorObj,
    } = useNewsletterPaginated({
        page: currentPage,
        search: searchQuery,
        pageSize: ITEMS_PER_PAGE,
    });

    const { data: statsData } = useNewsletterStats();
    const { remove, removeMany } = useNewsletterMutations();

    // ── Derived values ────────────────────────────────────────────────────────
    const subscribers = subscribersData?.subscribers ?? [];
    const totalItems = subscribersData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const totalSubscribers = statsData?.totalSubscribers ?? 0;
    const activeSubscribers = statsData?.activeSubscribers ?? 0;
    const inactiveSubscribers = statsData?.inactiveSubscribers ?? 0;
    const loading = subscribersLoading || subscribersIsFetching;

    // ── Toast helper ──────────────────────────────────────────────────────────
    const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setToast({ open: true, message, severity });
    };
    const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

    // ── Reset to page 1 ───────────────────────────────────────────────────────
    // FIX: Simplified — no cursor state to reset, just set page to 1.
    const resetToFirstPage = () => {
        setCurrentPage(1);
        setSelected(new Set());
    };

    // ── Pagination handler ────────────────────────────────────────────────────
    // FIX: Simplified — just set the page number directly. The seek-based
    // pagination in the service computes the correct Firestore offset from
    // the page number, so jumping to any page works correctly.
    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        setSelected(new Set()); // clear selection on page change
    };

    // ── Search ────────────────────────────────────────────────────────────────
    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setSearchQuery(searchInput);
            resetToFirstPage();
        }
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchQuery('');
        resetToFirstPage();
        showToast('Search cleared', 'info');
    };

    // ── Refresh ───────────────────────────────────────────────────────────────
    const handleRefresh = () => {
        resetToFirstPage();
        showToast('Refreshed', 'info');
    };

    // ── Single delete ─────────────────────────────────────────────────────────
    const handleDeleteClick = (subscriber: NewsletterSubscriber) => {
        setDeleteTarget(subscriber);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            setDeleteLoading(true);
            await remove.mutateAsync(deleteTarget.id);
            setDeleteTarget(null);
            resetToFirstPage();
            showToast('Subscriber deleted successfully', 'success');
        } catch (err: any) {
            showToast(`Failed to delete: ${err.message || 'Unknown error'}`, 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => setDeleteTarget(null);

    // ── Bulk select ───────────────────────────────────────────────────────────
    const allSelected = subscribers.length > 0 && selected.size === subscribers.length;
    const someSelected = selected.size > 0 && selected.size < subscribers.length;

    const handleSelectAll = () => {
        if (allSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(subscribers.map(s => s.id)));
        }
    };

    const handleSelectRow = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ── Bulk delete ───────────────────────────────────────────────────────────
    const handleBulkDeleteConfirm = async () => {
        const deletedCount = selected.size;
        try {
            setBulkDeleteLoading(true);
            await removeMany.mutateAsync(Array.from(selected));
            setBulkDeleteOpen(false);
            resetToFirstPage();
            showToast(`${deletedCount} subscriber(s) deleted`, 'success');
        } catch (err: any) {
            showToast(`Failed to delete: ${err.message || 'Unknown error'}`, 'error');
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    const hasActiveSearch = searchQuery !== '';

    if (loading && subscribers.length === 0) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
                <CircularProgress size={40} />
                <Typography variant="body1" sx={{ ml: 2 }}>Loading subscribers...</Typography>
            </Box>
        );
    }

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <EmailIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
                        <Typography variant="h5" gutterBottom>Access Denied</Typography>
                        <Typography variant="body1" color="text.secondary">
                            You don't have permission to view newsletter subscribers. Please contact your administrator.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: { md: '100%' },
                display: 'flex',
                flexDirection: 'column',
                overflow: { xs: 'visible', md: 'hidden' },
                p: { xs: 2, sm: 3 },
            }}
        >
            {/* ── Fixed Header ── */}
            <Box sx={{ flexShrink: 0 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <EmailIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                                Newsletter Subscribers
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your newsletter subscriber list
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        {canManage && selected.size > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteSweep />}
                                onClick={() => setBulkDeleteOpen(true)}
                                sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                            >
                                Delete ({selected.size})
                            </Button>
                        )}
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={loading}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* {subscribersIsError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {(subscribersErrorObj as Error)?.message || 'Failed to fetch subscribers'}
                    </Alert>
                )} */}

                {/* ── Stats Cards ── */}
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                                    Total Subscribers
                                </Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {totalSubscribers}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        All time signups
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                                    Active
                                </Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {activeSubscribers}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="success.main" noWrap>
                                        {totalSubscribers > 0
                                            ? `${((activeSubscribers / totalSubscribers) * 100).toFixed(1)}% of total`
                                            : '—'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                                    Inactive
                                </Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {inactiveSubscribers}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        Unsubscribed
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                                    {hasActiveSearch ? 'Matching Results' : 'This Page'}
                                </Typography>
                                <Typography variant="h5" component="div" color="text.primary"
                                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                                    {subscribers.length}
                                </Typography>
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {hasActiveSearch
                                            ? `of ${totalItems} total`
                                            : `page ${currentPage} of ${Math.max(1, totalPages)}`}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Search ── */}
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <TextField
                            placeholder="Search by email... (Press Enter)"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            sx={{ flexGrow: 1, maxWidth: { sm: 500 } }}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: hasActiveSearch && (
                                    <InputAdornment position="end">
                                        <Tooltip title="Clear search">
                                            <IconButton size="small" onClick={clearSearch}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {hasActiveSearch
                                ? `Found ${subscribers.length} matching results`
                                : `Showing ${subscribers.length} of ${totalItems} subscribers`}
                        </Typography>
                    </Stack>
                </Paper>
            </Box>

            {/* ── Scrollable Body ── */}
            <Box
                sx={{
                    flex: 1,
                    overflow: { xs: 'visible', md: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 'auto', md: 0 },
                }}
            >
                {subscribers.length === 0 && !loading && (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Card sx={{ width: '100%', maxWidth: 500 }}>
                            <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                                <Typography variant="h5" color="text.secondary" gutterBottom>
                                    {hasActiveSearch ? 'No Subscribers Match Your Search' : 'No Subscribers Found'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                                    {hasActiveSearch
                                        ? 'Try adjusting your search criteria.'
                                        : 'Newsletter subscribers will appear here once people sign up.'}
                                </Typography>
                                {hasActiveSearch && (
                                    <Button onClick={clearSearch} variant="outlined" startIcon={<ClearIcon />}>
                                        Clear Search
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {subscribers.length > 0 && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
                        <TableContainer
                            component={Paper}
                            ref={tableWrapperRef}
                            sx={{
                                flex: 1,
                                overflow: { xs: 'auto', md: 'auto' },
                                maxHeight: { md: '100%' },
                                mb: 2,
                                position: 'relative',
                                '& .MuiTable-root': { minWidth: { xs: 500, md: 700 } },
                            }}
                        >
                            <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                                <TableHead>
                                    <TableRow>
                                        {/* <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={someSelected}
                                                checked={allSelected}
                                                onChange={handleSelectAll}
                                                size="small"
                                            />
                                        </TableCell> */}
                                        <TableCell>Email</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            Subscribed At
                                        </TableCell>
                                        {canManage && <TableCell align="right">Actions</TableCell>}                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subscribers.map(subscriber => (
                                        <TableRow
                                            key={subscriber.id}
                                            hover
                                            selected={selected.has(subscriber.id)}
                                            sx={{
                                                '&:hover': { backgroundColor: 'action.hover' },
                                                '&:last-child td, &:last-child th': { borderBottom: 0 },
                                            }}
                                        >
                                            {/* <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selected.has(subscriber.id)}
                                                    onChange={() => handleSelectRow(subscriber.id)}
                                                    size="small"
                                                />
                                            </TableCell> */}

                                            <TableCell>
                                                <Typography
                                                    fontWeight={500}
                                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                >
                                                    {subscriber.email}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: { xs: 'block', sm: 'none' } }}
                                                >
                                                    {formatDate(subscriber.subscribedAt)}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Chip
                                                    label={subscriber.isActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={getSoftChipSx(subscriber.isActive ? 'success' : 'default', isDark)}
                                                />
                                            </TableCell>

                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(subscriber.subscribedAt)}
                                                </Typography>
                                            </TableCell>

                                            {canManage && (
                                                <TableCell align="right">
                                                    <Tooltip title="Delete Subscriber">
                                                        <IconButton color="error" size="small" onClick={() => handleDeleteClick(subscriber)}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

                        <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
                            <Box sx={{ py: 2, px: 2 }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1.5, sm: 3 }}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ width: '100%' }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        Showing {totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1}–
                                        {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} subscribers
                                    </Typography>
                                    <Pagination
                                        count={Math.max(1, totalPages)}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size={isMobile ? 'small' : 'medium'}
                                        showFirstButton
                                        showLastButton
                                        disabled={loading || totalPages <= 1}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        Page {currentPage} of {Math.max(1, totalPages)}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* ── Single Delete Confirmation Dialog ── */}
            <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Subscriber</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete{' '}
                        <strong>{deleteTarget?.email}</strong>?{' '}
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Bulk Delete Confirmation Dialog ── */}
            <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete {selected.size} Subscriber(s)</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{selected.size}</strong> selected subscriber(s)?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleteLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleBulkDeleteConfirm}
                        disabled={bulkDeleteLoading}
                        startIcon={bulkDeleteLoading ? <CircularProgress size={16} /> : <DeleteSweep />}
                    >
                        Delete All
                    </Button>
                </DialogActions>
            </Dialog>

            <Backdrop
                open={deleteLoading}
                sx={{
                    zIndex: (t) => t.zIndex.drawer + 200,
                    flexDirection: 'column',
                    gap: 3,
                    bgcolor: 'rgba(0,0,0,0.6)',
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        px: 5,
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                        minWidth: 280,
                        boxShadow: 24,
                    }}
                >
                    <CircularProgress size={48} thickness={4} />
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
                        >
                            Deleting Email…
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
                        >
                            Please wait, do not close this window
                        </Typography>
                    </Box>
                </Box>
            </Backdrop>

            <ToastNotification
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={handleCloseToast}
            />
        </Box>
    );
};

export default NewsletterManagement;